import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  LinearProgress,
  CircularProgress,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  Logout,
  PieChart,
  Receipt,
  ShowChart,
  Timeline,
  Visibility,
  SavingsOutlined,
} from '@mui/icons-material';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PARTNER_LOGIN } from '../../../services/constants/URIConstants';
import { getPartnerDashboard } from '../../../services/request/PartnerPortal';
import type { PartnerActivityItem, PartnerPortalDashboard, PartnerStockAllocation } from '../../../models/PartnerPortalTypes';
import { formatCurrency } from '../../../utils/CurrencyFormat';
import { planStatusChipColor, planStatusLabel } from '../../../models/StockPickTypes';
import { formatPartnerPhoneDisplay } from '../../../utils/partnerPortalFormat';
import { ReleasepilotAffiliation } from '../../../shared-components/branding/ReleasepilotAffiliation';
import PartnerAllocationDetailDialog from './PartnerAllocationDetailDialog';
import PartnerLendingVisibilityPanel from './PartnerLendingVisibilityPanel';
import { getPartnerPortalControlSnapshot } from '../../../services/request/CapitalAnalytics';
import type { PartnerControlSnapshot } from '../../../models/CapitalAnalyticsTypes';

function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function activityIcon(type: PartnerActivityItem['type']) {
  switch (type) {
    case 'ALLOCATION':
      return <ShowChart fontSize="small" color="primary" />;
    case 'CONTRIBUTION':
      return <AccountBalanceWallet fontSize="small" color="success" />;
    case 'PLAN_STATUS':
      return <Timeline fontSize="small" color="warning" />;
    default:
      return <Receipt fontSize="small" />;
  }
}

const PartnerDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [, , removeCookie] = useCookies(['auth_token', 'user_role', 'partner_id']);
  const [dashboard, setDashboard] = useState<PartnerPortalDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState(0);
  const [detailAlloc, setDetailAlloc] = useState<PartnerStockAllocation | null>(null);
  const [lendingSnapshot, setLendingSnapshot] = useState<PartnerControlSnapshot | null>(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDashboard(await getPartnerDashboard());
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (!dashboard?.profile) return;
    getPartnerPortalControlSnapshot()
      .then(setLendingSnapshot)
      .catch(() => setLendingSnapshot(null));
  }, [dashboard?.profile?.id]);

  const portfolioTotals = useMemo(() => {
    const rows = dashboard?.allocations ?? [];
    let currentValue = 0;
    let invested = 0;
    for (const row of rows) {
      if (row.planStatus === 'HARVESTED') continue;
      const mv = row.marketValuation;
      if (mv?.priceAvailable) {
        currentValue += mv.partnerCurrentValue ?? mv.currentValue * (row.shareOfPlanPct / 100);
        invested += mv.partnerInvestedAmount ?? row.allocatedAmount;
      } else {
        invested += row.allocatedAmount;
        currentValue += row.allocatedAmount;
      }
    }
    return { currentValue, invested, unrealizedPnl: currentValue - invested };
  }, [dashboard?.allocations]);

  const handleLogout = () => {
    removeCookie('auth_token', { path: '/' });
    removeCookie('user_role', { path: '/' });
    removeCookie('partner_id', { path: '/' });
    navigate(PARTNER_LOGIN);
    toast.info('Signed out');
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !dashboard) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Partner profile not found. Sign in again.'}</Alert>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button onClick={() => loadDashboard()}>Retry</Button>
          <Button onClick={() => navigate(PARTNER_LOGIN)}>Back to login</Button>
        </Stack>
      </Container>
    );
  }

  const { profile, allocations, contributions, activity } = dashboard;
  const deployedPct =
    profile.totalContributed > 0
      ? Math.round((profile.totalAllocated / profile.totalContributed) * 100)
      : 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <PieChart sx={{ mr: 1.5 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Partner Portal
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {profile.name}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={handleLogout} aria-label="Sign out">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ pb: 6 }}>
        <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
          This portal is read-only. View stock allocations, lending positions, contributions, and plan
          details. Contact your fund manager for changes.
        </Alert>

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Total contributed
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(profile.totalContributed)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: 'primary.main' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Allocated to stocks
                </Typography>
                <Typography variant="h5" fontWeight={700} color="primary.main">
                  {formatCurrency(profile.totalAllocated)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={deployedPct}
                  sx={{ mt: 1, height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {deployedPct}% of corpus deployed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: '#E65100' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  In lending
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#E65100' }}>
                  {formatCurrency(lendingSnapshot?.lendingInvested ?? 0)}
                </Typography>
                {(lendingSnapshot?.lendingInvested ?? 0) > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {lendingSnapshot?.partnerLendingRatePctMonthly ?? 1.5}%/mo · est.{' '}
                    {formatCurrency(lendingSnapshot?.monthlyPartnerLendingIncome ?? 0)}/mo
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderLeft: 4, borderColor: portfolioTotals.unrealizedPnl >= 0 ? 'success.main' : 'error.main' }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Stock portfolio value
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(portfolioTotals.currentValue)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: portfolioTotals.unrealizedPnl >= 0 ? 'success.main' : 'error.main',
                    fontWeight: 600,
                  }}
                >
                  Unrealised {portfolioTotals.unrealizedPnl >= 0 ? '+' : ''}
                  {formatCurrency(portfolioTotals.unrealizedPnl)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography variant="caption" color="text.secondary">
                  Unallocated (cash)
                </Typography>
                <Typography variant="h5" fontWeight={700}>
                  {formatCurrency(profile.availableBalance)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper sx={{ mt: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label="Stock allocations" icon={<ShowChart />} iconPosition="start" />
            <Tab label="Lending" icon={<SavingsOutlined />} iconPosition="start" />
            <Tab label="Contributions" icon={<AccountBalanceWallet />} iconPosition="start" />
            <Tab label="Activity" icon={<Timeline />} iconPosition="start" />
            <Tab label="Profile" />
          </Tabs>

          <Box sx={{ px: { xs: 1, sm: 2 } }}>
            <TabPanel value={tab} index={0}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Where your capital is working — by stock and investment plan. Other partners on the same plan
                are not shown.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Stock</TableCell>
                      <TableCell>Plan</TableCell>
                      <TableCell>Stage</TableCell>
                      <TableCell>Holding</TableCell>
                      <TableCell align="right">Your amount</TableCell>
                      <TableCell align="right">Current value</TableCell>
                      <TableCell align="right">P&amp;L</TableCell>
                      <TableCell align="right">Share</TableCell>
                      <TableCell width={56} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allocations.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                            No allocations yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {allocations.map((row) => {
                      const mv = row.marketValuation;
                      const currentVal =
                        mv?.priceAvailable && row.planStatus !== 'HARVESTED'
                          ? mv.partnerCurrentValue ?? 0
                          : null;
                      const pnl =
                        currentVal != null
                          ? currentVal - row.allocatedAmount
                          : row.pnl.unrealizedProfit - row.pnl.unrealizedLoss;
                      const pnlColor = pnl >= 0 ? 'success.main' : 'error.main';
                      return (
                      <TableRow key={row.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {row.stockName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.nseCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.planCode}</Typography>
                          {row.planLabel && (
                            <Typography variant="caption" color="text.secondary">
                              {row.planLabel}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={planStatusLabel(row.planStatus)}
                            size="small"
                            color={planStatusChipColor(row.planStatus)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{row.holdingTypeLabel}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {row.allocatedAmount > 0 ? formatCurrency(row.allocatedAmount) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {currentVal != null ? (
                            <Typography fontWeight={600}>{formatCurrency(currentVal)}</Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                          {mv?.lastClosePrice != null && row.planStatus !== 'HARVESTED' && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              @ {formatCurrency(mv.lastClosePrice)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row.planStatus === 'HARVESTED' ? (
                            <Typography variant="body2" color="text.secondary">
                              Realised
                            </Typography>
                          ) : currentVal != null ? (
                            <Typography fontWeight={600} sx={{ color: pnlColor }}>
                              {pnl >= 0 ? '+' : ''}
                              {formatCurrency(pnl)}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              —
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{row.shareOfPlanPct.toFixed(0)}%</TableCell>
                        <TableCell>
                          <IconButton size="small" onClick={() => setDetailAlloc(row)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Allocation by sector (preview)
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {Array.from(
                    allocations.reduce((map, a) => {
                      const sector = a.domain || 'Other';
                      map.set(sector, (map.get(sector) ?? 0) + a.allocatedAmount);
                      return map;
                    }, new Map<string, number>())
                  ).map(([sector, amt]) =>
                    amt > 0 ? (
                      <Chip
                        key={sector}
                        label={`${sector}: ${formatCurrency(amt)}`}
                        variant="outlined"
                      />
                    ) : null
                  )}
                </Stack>
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Capital deployed to lending — your agreed rate and active positions. Borrower identities
                are not shown here.
              </Typography>
              <PartnerLendingVisibilityPanel
                partnerId={profile.id}
                partnerName={profile.name}
                showOperatorEconomics={false}
                portalMode
              />
            </TabPanel>

            <TabPanel value={tab} index={2}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Reference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {contributions.map((c) => (
                      <TableRow key={c.id}>
                        <TableCell>{c.contributionDate}</TableCell>
                        <TableCell align="right">{formatCurrency(c.amount)}</TableCell>
                        <TableCell>{c.referenceNote ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>

            <TabPanel value={tab} index={3}>
              <Stack spacing={1.5}>
                {activity.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{ mt: 0.25 }}>{activityIcon(item.type)}</Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                          <Typography variant="subtitle2">{item.title}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.date}
                          </Typography>
                        </Stack>
                        {item.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                            {item.description}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </TabPanel>

            <TabPanel value={tab} index={4}>
              <Paper variant="outlined" sx={{ p: 2, maxWidth: 560 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Capital summary
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Total contributed
                    </Typography>
                    <Typography fontWeight={600}>{formatCurrency(profile.totalContributed)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Allocated to stocks
                    </Typography>
                    <Typography fontWeight={600}>{formatCurrency(profile.totalAllocated)}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      In lending
                    </Typography>
                    <Typography fontWeight={600} sx={{ color: '#E65100' }}>
                      {formatCurrency(lendingSnapshot?.lendingInvested ?? 0)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Unallocated cash
                    </Typography>
                    <Typography fontWeight={600}>{formatCurrency(profile.availableBalance)}</Typography>
                  </Grid>
                </Grid>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Account
                </Typography>
                <Stack spacing={1}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Name
                    </Typography>
                    <Typography>{profile.name}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Email
                    </Typography>
                    <Typography>{profile.email}</Typography>
                  </Box>
                  {profile.phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography>
                        {profile.phone ? formatPartnerPhoneDisplay(profile.phone) : '—'}
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Status
                    </Typography>
                    <Chip
                      label={profile.status}
                      size="small"
                      color={profile.status === 'ACTIVE' ? 'success' : 'default'}
                    />
                  </Box>
                  {profile.memberSince && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Member since
                      </Typography>
                      <Typography>{profile.memberSince}</Typography>
                    </Box>
                  )}
                </Stack>
                <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                  Your portal password is set by the fund manager. Contact admin to reset if needed.
                </Alert>
              </Paper>
            </TabPanel>
          </Box>
        </Paper>
      </Container>

      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <ReleasepilotAffiliation />
      </Box>

      <PartnerAllocationDetailDialog
        open={Boolean(detailAlloc)}
        allocation={detailAlloc}
        onClose={() => setDetailAlloc(null)}
      />
    </Box>
  );
};

export default PartnerDashboard;
